import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Tag, Trash2, Wallet } from "lucide-react";
import { api, formatApiError } from "../api";
import { PLANS } from "../constants/testIds";
import { useAppData } from "../contexts/AppDataContext";
import { formatCurrency } from "../lib/format";
import { fadeUp } from "../lib/motion";
import { useSortPersistence } from "../hooks/useSortPersistence";
import { Button } from "../components/ui/button";
import { StatusPill } from "../components/common/StatusPill";
import { GymLoader } from "../components/common/GymLoader";
import { ErrorState } from "../components/common/ErrorState";
import { SortControl } from "../components/common/SortControl";
import { PlanFormDialog } from "../components/plans/PlanFormDialog";
import { PromotionFormDialog } from "../components/plans/PromotionFormDialog";
import { toast } from "../components/ui/sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const TABS = [{ key: "plans", label: "Plans" }, { key: "promotions", label: "Promotions" }];
const PRICING_TYPE_LABEL = { subscription: "Subscription", session_pack: "Session pack", pay_per_class: "Pay per class" };
const SORT_FIELDS = [
  { value: "id", label: "Newest first", type: "date" },
  { value: "name", label: "Name (A–Z)", type: "alpha" },
  { value: "amount", label: "Price", type: "numeric" },
  { value: "active_member_count", label: "Members on plan", type: "numeric" },
];

// UX-GAP-01 §1.3: Owner Cockpit "Memberships & Plans" — was a StubPage. Two tabs against the
// pre-existing `/plans` + `/promotions` CRUD.
export default function Plans() {
  const { branches } = useAppData();
  const reducedMotion = useReducedMotion();
  const [tab, setTab] = useState("plans");
  const [planFormOpen, setPlanFormOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [promoFormOpen, setPromoFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const queryClient = useQueryClient();
  // DEFECT-D-29/D-30: default newest-first; persisted per-screen for the session.
  const { sortBy, sortDir, setSort } = useSortPersistence("plans-list", "id", "desc");

  const plansQ = useQuery({
    queryKey: ["plans-list", sortBy, sortDir],
    queryFn: async () => (await api.get("/plans", { params: { sort_by: sortBy === "id" ? undefined : sortBy, sort_dir: sortDir } })).data.plans,
    enabled: tab === "plans",
  });
  const promosQ = useQuery({ queryKey: ["promotions-list"], queryFn: async () => (await api.get("/promotions")).data.promotions, enabled: tab === "promotions" });

  const deleteMutation = useMutation({
    mutationFn: (planId) => api.delete(`/plans/${planId}`),
    onSuccess: () => {
      toast.success("Plan deleted.");
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ["plans-list"] });
    },
    onError: (e) => { toast.error(formatApiError(e).message); setDeleteTarget(null); },
  });

  return (
    <div className="space-y-5 max-w-4xl" data-testid={PLANS.page}>
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Memberships & Plans</h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>What members can buy, and what discounts are on offer.</p>
        </div>
        {tab === "plans" ? (
          <Button data-testid={PLANS.addPlanButton} onClick={() => { setEditingPlan(null); setPlanFormOpen(true); }} className="text-white gap-2" style={{ background: "var(--scarlet-accent)" }}>
            <Wallet size={15} /> Add plan
          </Button>
        ) : (
          <Button data-testid={PLANS.addPromoButton} onClick={() => setPromoFormOpen(true)} className="text-white gap-2" style={{ background: "var(--scarlet-accent)" }}>
            <Tag size={15} /> Add promotion
          </Button>
        )}
      </motion.div>

      <div className="flex gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.key} data-testid={PLANS.tab(t.key)} onClick={() => setTab(t.key)}
            className="text-sm font-medium rounded-[10px] px-3.5 py-1.5 transition-colors"
            style={tab === t.key ? { background: "var(--scarlet-accent)", color: "#fff" } : { background: "transparent", border: "1px solid var(--scarlet-hairline)", color: "var(--scarlet-text-secondary)" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "plans" && (
        <>
          {plansQ.isError && <ErrorState error={plansQ.error} onRetry={() => plansQ.refetch()} />}
          {plansQ.isLoading && <GymLoader label="Loading plans…" />}
          {plansQ.data && plansQ.data.length === 0 && (
            <div className="solid-surface rounded-[14px] p-10 text-center text-sm" data-testid={PLANS.emptyState} style={{ color: "var(--scarlet-text-secondary)" }}>No plans yet.</div>
          )}
          {plansQ.data && plansQ.data.length > 0 && (
            <>
              <div className="flex justify-end">
                <SortControl fields={SORT_FIELDS} sortBy={sortBy} sortDir={sortDir} onChange={setSort} testId={PLANS.sortControl} />
              </div>
              <div className="solid-surface rounded-[14px] overflow-hidden">
                <table className="w-full text-sm" data-testid={PLANS.table}>
                  <thead>
                    <tr className="text-left" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                      <th className="eyebrow px-5 py-3">Plan</th>
                      <th className="eyebrow px-5 py-3">Type</th>
                      <th className="eyebrow px-5 py-3">Price</th>
                      <th className="eyebrow px-5 py-3">Duration</th>
                      <th className="eyebrow px-5 py-3">Version</th>
                      <th className="eyebrow px-5 py-3">Members</th>
                      <th className="eyebrow px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {plansQ.data.map((p) => (
                      <tr key={p.id} data-testid={PLANS.row(p.id)} style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                        <td className="px-5 py-3.5 font-medium" style={{ color: "var(--scarlet-text)" }}>{p.name}</td>
                        <td className="px-5 py-3.5" style={{ color: "var(--scarlet-text-secondary)" }}>{PRICING_TYPE_LABEL[p.pricing.type]}</td>
                        <td className="px-5 py-3.5 tabular-nums-force font-medium" style={{ color: "var(--scarlet-text)" }}>{formatCurrency(p.pricing.amount)}</td>
                        <td className="px-5 py-3.5" style={{ color: "var(--scarlet-text-secondary)" }}>{p.duration ? `${p.duration} days` : "—"}</td>
                        <td className="px-5 py-3.5"><StatusPill status="active" label={`v${p.version}`} /></td>
                        <td className="px-5 py-3.5 tabular-nums-force" data-testid={PLANS.activeMemberCount(p.id)} style={{ color: "var(--scarlet-text-secondary)" }}>{p.active_member_count}</td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="inline-flex gap-2">
                            <Button size="sm" variant="outline" data-testid={PLANS.editButton(p.id)} onClick={() => { setEditingPlan(p); setPlanFormOpen(true); }}>Edit</Button>
                            <Button size="sm" variant="outline" data-testid={PLANS.deleteButton(p.id)} onClick={() => setDeleteTarget(p)} className="gap-1" style={{ color: "var(--scarlet-neg)" }}>
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {tab === "promotions" && (
        <>
          {promosQ.isError && <ErrorState error={promosQ.error} onRetry={() => promosQ.refetch()} />}
          {promosQ.isLoading && <GymLoader label="Loading promotions…" />}
          {promosQ.data && promosQ.data.length === 0 && (
            <div className="solid-surface rounded-[14px] p-10 text-center text-sm" data-testid={PLANS.emptyState} style={{ color: "var(--scarlet-text-secondary)" }}>No promotions yet.</div>
          )}
          {promosQ.data && promosQ.data.length > 0 && (
            <div className="solid-surface rounded-[14px] overflow-hidden" data-testid={PLANS.promoTable}>
              {promosQ.data.map((p) => (
                <div key={p.id} data-testid={PLANS.promoRow(p.id)} className="flex items-center justify-between px-5 py-3.5" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                  <div>
                    <p className="font-semibold" style={{ color: "var(--scarlet-text)" }}>{p.code} <span className="ml-1 font-normal" style={{ color: "var(--scarlet-text-secondary)" }}>{p.rules?.discount_percent ?? "—"}% off</span></p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>{p.valid_window.start?.slice(0, 10)} → {p.valid_window.end?.slice(0, 10)}</p>
                  </div>
                  <StatusPill status={p.branch_scope ? "trial" : "active"} label={p.branch_scope ? `${p.branch_scope.length} branch${p.branch_scope.length > 1 ? "es" : ""}` : "Org-wide"} />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <PlanFormDialog open={planFormOpen} onOpenChange={setPlanFormOpen} plan={editingPlan} />
      <PromotionFormDialog open={promoFormOpen} onOpenChange={setPromoFormOpen} branches={branches} />
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid={PLANS.deleteDialog}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.active_member_count > 0
                ? `${deleteTarget.active_member_count} active member(s) are on this plan — it can't be deleted until none are linked.`
                : "This plan has zero active members and can be safely deleted. Historical memberships that used it keep their original plan details."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid={PLANS.deleteConfirmButton} disabled={deleteTarget?.active_member_count > 0 || deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(deleteTarget.id)} className="text-white" style={{ background: "var(--scarlet-neg)" }}
            >
              Delete plan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
